import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import PropTypes from "prop-types";

const Wrapper = styled.div`
  position: relative;
  width: 100%;
`;

const Control = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #ffffff;
  padding: 10px 12px;
  border: 1px solid #d0d0d0;
  border-radius: 8px;
  cursor: pointer;
  transition: 0.15s ease;
  width: 100%;
  position: relative;
  overflow: hidden;

  &:hover {
    border-color: #888;
  }
`;

const Placeholder = styled.span`
  color: #aaa;
`;

const Arrow = styled.span`
  transition: 0.2s ease;
  transform: ${(props) => (props.open ? "rotate(-180deg)" : "rotate(0)")};
`;

const Menu = styled.div`
  position: absolute;
  top: 110%;
  left: 0;
  width: 100%;
  background: #ffffff;
  border: 1px solid #dcdcdc;
  border-radius: 10px;
  box-shadow: 0px 8px 15px rgba(0, 0, 0, 0.08);
  padding: 6px 0;
  z-index: 10;
  max-height: 240px;
  overflow-y: auto;
`;

const SearchInput = styled.input`
  width: calc(100% - 20px);
  margin: 4px 10px;
  padding: 6px 8px;
  border: 1px solid #ccc;
  border-radius: 6px;
  outline: none;

  &:focus {
    border-color: #888;
  }
`;

const Item = styled.label`
  display: flex;
  align-items: center;
  padding: 8px 14px;
  cursor: pointer;
  transition: background 0.15s ease;
  font-weight: ${(props) => (props.all ? "600" : "normal")};

  &:hover {
    background: #f5f5f5;
  }

  input {
    margin-right: 10px;
  }
`;

const Divider = styled.div`
  height: 1px;
  background: #e5e5e5;
  margin: 6px 0;
`;

const DropdownCheckbox = ({
  options = [],
  value = [],
  onChange,
  placeholder = "Select options",
}) => {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showCount, setShowCount] = useState(false);
  const ref = useRef(null);
  const controlRef = useRef(null);
  const hiddenMeasureRef = useRef(null);

  const allSelected =
    options.length > 0 && options.every((opt) => value.includes(opt.value));

  const toggleSelectAll = () => {
    if (allSelected) {
      onChange([]);
    } else {
      onChange(options.map((opt) => opt.value));
    }
  };

  const toggleOption = (val) => {
    if (value.includes(val)) {
      onChange(value.filter((v) => v !== val));
    } else {
      onChange([...value, val]);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
        setSearchTerm(""); // clear search when closing
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // T027 [US8]: Measure whether the full joined label text overflows the control width.
  // Use a hidden off-screen span to measure text width vs the available control width,
  // so we never hard-code a count threshold — actual pixel fit determines fallback.
  // Values without a matching option (e.g. a deleted dealership still linked to an offer)
  // are kept in `value` but not shown.
  const selectedOptions = value
    .map((v) => options.find((o) => o.value === v))
    .filter(Boolean);
  const fullLabel = selectedOptions.map((opt) => opt.label).join(", ");

  useEffect(() => {
    if (!selectedOptions.length || !hiddenMeasureRef.current || !controlRef.current) {
      setShowCount(false);
      return;
    }
    // hiddenMeasureRef always contains the full text (visibility: hidden, no wrapping)
    // controlRef is the Control div; subtract ~36px for the arrow + its gap
    const textWidth = hiddenMeasureRef.current.scrollWidth;
    const availableWidth = controlRef.current.clientWidth - 36;
    setShowCount(textWidth > availableWidth);
  }, [value, options]);

  // Filtered options based on search term
  const filteredOptions = options.filter((opt) =>
    opt.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Wrapper ref={ref}>
      <Control onClick={() => setOpen(!open)} ref={controlRef}>
        {selectedOptions.length ? (
          <>
            {/* Hidden span used only for pixel-width measurement — never visible to user */}
            <span
              ref={hiddenMeasureRef}
              aria-hidden="true"
              style={{
                position: "absolute",
                visibility: "hidden",
                whiteSpace: "nowrap",
                pointerEvents: "none",
              }}
            >
              {fullLabel}
            </span>
            {/* Visible label: full text when it fits, "N selected" when it overflows */}
            <span
              style={{
                flex: 1,
                minWidth: 0,
                overflow: "hidden",
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
              }}
              title={fullLabel}
            >
              {showCount ? `${selectedOptions.length} selected` : fullLabel}
            </span>
          </>
        ) : (
          <Placeholder>{placeholder}</Placeholder>
        )}
        <Arrow open={open}>▾</Arrow>
      </Control>

      {open && (
        <Menu>
          <SearchInput
            type="text"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            autoFocus
          />

          {/* Select All */}
          <Item all>
            <input
              type="checkbox"
              checked={allSelected}
              onChange={toggleSelectAll}
            />
            {allSelected ? "Unselect All" : "Select All"}
          </Item>

          <Divider />

          {/* Options */}
          {filteredOptions.map((opt) => (
            <Item key={opt.value}>
              <input
                type="checkbox"
                checked={value.includes(opt.value)}
                onChange={() => toggleOption(opt.value)}
              />
              {opt.label}
            </Item>
          ))}
        </Menu>
      )}
    </Wrapper>
  );
};

DropdownCheckbox.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
    })
  ).isRequired,
  value: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  ).isRequired,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
};

export default DropdownCheckbox;
